import { track } from '../api/track-event';
import klaviyoApi from '../api/klaviyoApi';

/**
 * Page-level analytics shared by both Vite entries.
 *
 * Klaviyo flows and segments filter on the legacy vue-router route name, so every signal keeps that
 * as its `key` whichever world rendered the page. `path` is sent alongside it; it never replaces the
 * key. The routers are passed in rather than imported, so neither entry bundles the other's router.
 */

// Sentinel rather than undefined, so the first page view always pushes even when the key is unset.
const NO_KEY_YET = Symbol('no-key-yet');

let lastDataLayerKey = NO_KEY_YET;

const pushToDataLayer = (entry) => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(entry);
};

/**
 * Claim the once-per-session Klaviyo "Visited page" beacon for a page key, returning whether this
 * call won the claim.
 *
 * EN-958: the guard used to read `klaviyovisited: ${name}` (with a space after the colon) and write
 * `klaviyovisited:${name}` (without), so the read never found the key the previous navigation had
 * just written. The dedupe never engaged once, and the beacon fired on every single navigation.
 * Reading and writing now share one key expression so they cannot drift apart again.
 *
 * Callers must check authentication BEFORE calling this - it has a write side effect, so claiming
 * for an anonymous user would burn the claim and suppress the beacon for that page once they log in.
 *
 * @param {string|undefined} key Legacy route name.
 * @returns {boolean}
 */
export const claimKlaviyoVisit = (key) => {
	const storageKey = `klaviyovisited:${key}`;

	try {
		if (sessionStorage.getItem(storageKey) !== null) {
			return false;
		}

		sessionStorage.setItem(storageKey, 'true');
	} catch {
		// Storage unavailable or full - Safari Private mode, a cookie-blocked iframe, quota exceeded.
		// Exactly the privacy-hardened browsers EN-958 was about, and this runs on every navigation.
		// Claim anyway: the dedupe is an optimisation, and a redundant beacon costs less than silently
		// losing this page's visit data. Any request failure it causes is swallowed by sendKlaviyoEvent.
	}

	return true;
};

/**
 * Push the user's id to GTM's dataLayer without counting it as a page view.
 *
 * The SPA calls this once its session has hydrated. On the Inertia onboarding handoff the first
 * page view can land before /api/user returns, and would otherwise leave GTM without a user id
 * until the route name next changed.
 *
 * @param {{ id: number }|null} user
 */
export const pushDataLayerUser = (user) => {
	pushToDataLayer({ user_id: user?.id });
};

/**
 * Fire the three page-level signals for one page view.
 *
 * - GTM dataLayer `{ user_id, path }`, only when the key changes. The SPA used to push this from a
 *   route-name watcher in App.vue, and a vue-router afterEach also fires on query-only changes.
 * - `/api/track-event` `page_visit` with the key as `action_description`, which HomeBaselineService
 *   and UserActionController match on, and the path in `metadata`. Signed-in users only.
 * - Klaviyo "Visited page" `{ page, path }`, once per key per session. Signed-in users only.
 *
 * @param {{ key: string|undefined, path: string, user: { id: number, email?: string }|null }} pageView
 */
export const trackPageView = ({ key, path, user }) => {
	if (key !== lastDataLayerKey) {
		lastDataLayerKey = key;
		pushToDataLayer({ user_id: user?.id, path });
	}

	if (!user) {
		return;
	}

	track({ action_type: 'page_visit', action_description: key, metadata: { path } });

	if (user.email && claimKlaviyoVisit(key)) {
		klaviyoApi.sendKlaviyoEvent(user.email, key, path);
	}
};

/**
 * SPA: one page view per vue-router navigation, keyed by route name.
 *
 * afterEach also runs for a navigation a guard aborted or that duplicated the current route, where
 * `to` is a page the user never reached. Those are skipped.
 *
 * @param {import('vue-router').Router} router
 * @param {() => { id: number, email?: string }|null} getUser Read at navigation time.
 * @returns {() => void} Removes the hook.
 */
export const trackSpaPageViews = (router, getUser) => {
	return router.afterEach((to, from, failure) => {
		if (failure) {
			return;
		}

		trackPageView({ key: to.name, path: to.path, user: getUser() });
	});
};

/**
 * Inertia: one page view per `navigate` event, keyed by the page component's legacy route name.
 * Components without an entry in `keys` emit nothing.
 *
 * @param {{ on: Function }} router The Inertia router.
 * @param {Record<string, string>} keys Page component -> legacy route name.
 * @returns {() => void} Removes the listener.
 */
export const trackInertiaPageViews = (router, keys) => {
	return router.on('navigate', (event) => {
		const { component, url, props } = event.detail.page;
		const key = keys[component];

		if (!key) {
			return;
		}

		trackPageView({
			key,
			path: new URL(url, window.location.origin).pathname,
			user: props?.auth?.user ?? null,
		});
	});
};
